// THE NEEDS-ATTENTION WORKLIST — grouping by CAUSE. PURE (`now` injected).
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md §4, D8, D9e step 3
//
// ⚠️⚠️ THIS IS A PRESENTATION OF THE ACCOUNT ROWS, NOT A REPLACEMENT FOR THEM.
// D8's row is the kyck account and stays so: a group CARRIES its members rather
// than standing in for them, so the page can expand one and the underlying truth
// is never discarded on the way to a tidier list.
//
// ⚠️ THE ANTI-REQUIREMENT IT EXISTS FOR: one dead unit must not produce N red
// rows. Measured 2026-09-16 that is not hypothetical — the largest shared Meta ad
// account carries 14 kyck accounts, and the largest Google Ads MCC 56 customers.
// A worklist that turns one outage into 56 rows is destroyed by the first real
// outage, which is precisely when it is needed.

import type { AccountHealth, SourceExpectation } from '../../types/sourceHealth.js';
import { accountStatus, STATUS_RANK, type HealthStatus, type StatusVerdict } from './status.js';

/** One account, in trouble on its own. */
export interface WorklistAccountEntry {
  kind: 'account';
  status: HealthStatus;
  reason: string | null;
  account: AccountHealth;
}

/** Several accounts in trouble from ONE cause. */
export interface WorklistGroupEntry {
  kind: 'group';
  status: HealthStatus;
  reason: string | null;
  causeKey: string;
  causeLabel: string;
  /**
   * ⚠️ THE MEMBERS IN TROUBLE, not every account under the cause. A cause row
   * saying "14 locations" when four are fine overstates the outage, and the four
   * would then be listed under a heading that does not describe them.
   */
  members: AccountHealth[];
  /** Always `members.length`. Carried so a collapsed row need not count. */
  memberCount: number;
}

export type WorklistEntry = WorklistAccountEntry | WorklistGroupEntry;

/**
 * Build the Needs-attention list.
 *
 * ⚠️⚠️ GREEN ACCOUNTS NEVER ENTER, AND THAT IS WHAT KEEPS DORMANCY OUT. A Google
 * Ads account with no recent spend resolves green (`freshnessMeaningful: false`,
 * D9c), so it cannot reach a group — there is no dormancy rule here because there
 * does not need to be one. The exclusion happens upstream, in `accountStatus`,
 * where it belongs. ⚠️ See the KNOWN GAP note below for Meta, where it does NOT
 * yet hold.
 *
 * ⚠️ A GROUP OF ONE IS A PLAIN ACCOUNT ROW. "Shared ad account — 1 location" is
 * a heading over a single item: it costs a line, adds a level of nesting, and
 * tells the reader nothing the account row does not. The cause is still visible
 * on the account row itself (`causeLabel`).
 */
export function buildWorklist(
  accounts: readonly AccountHealth[],
  expectation: SourceExpectation,
  nowMs: number,
  /**
   * ⚠️ Is the source's own pull behind? Passed through to `accountStatus` so the
   * worklist and the grid cannot disagree about whether an outage is one finding
   * or N. Required, for the reason given at `accountStatus`.
   */
  sourceStale: boolean,
): WorklistEntry[] {
  const troubled: Array<{ account: AccountHealth; verdict: StatusVerdict }> = [];
  for (const account of accounts) {
    const verdict = accountStatus(account, expectation, nowMs, sourceStale);
    if (verdict.status === 'green') continue;
    troubled.push({ account, verdict });
  }

  // Bucket by cause, PRESERVING FIRST-SEEN ORDER so the output is deterministic
  // for a given input rather than depending on Map iteration of a mixed key set.
  const byCause = new Map<string, Array<{ account: AccountHealth; verdict: StatusVerdict }>>();
  const ungrouped: Array<{ account: AccountHealth; verdict: StatusVerdict }> = [];
  for (const item of troubled) {
    const key = item.account.causeKey;
    if (key === null) {
      ungrouped.push(item);
      continue;
    }
    const bucket = byCause.get(key);
    if (bucket) bucket.push(item);
    else byCause.set(key, [item]);
  }

  const entries: WorklistEntry[] = [];
  for (const [causeKey, members] of byCause) {
    if (members.length === 1) {
      // A group of one is a plain row — see the header.
      const only = members[0]!;
      entries.push({ kind: 'account', status: only.verdict.status, reason: only.verdict.reason, account: only.account });
      continue;
    }
    // ⚠️ THE WORST MEMBER SETS THE GROUP'S COLOUR AND ITS REASON, and the two come
    // from the SAME member rather than being resolved separately — a group headed
    // "red" above an amber account's sentence describes neither.
    const worst = members.reduce((a, b) =>
      STATUS_RANK[b.verdict.status] < STATUS_RANK[a.verdict.status] ? b : a,
    );
    entries.push({
      kind: 'group',
      status: worst.verdict.status,
      reason: worst.verdict.reason,
      causeKey,
      // ⚠️ Falls back to the key rather than to a friendly invention: a label we
      // cannot resolve should look unresolved, not plausible.
      causeLabel: worst.account.causeLabel ?? causeKey,
      members: members.map((m) => m.account),
      memberCount: members.length,
    });
  }
  for (const item of ungrouped) {
    entries.push({ kind: 'account', status: item.verdict.status, reason: item.verdict.reason, account: item.account });
  }

  return sortWorklist(entries);
}

/**
 * Worst first, then widest blast radius, then stable by name.
 *
 * ⚠️ MEMBER COUNT BEFORE NAME, because the page is a worklist: an outage taking
 * out fourteen locations should sit above one account that is a day behind, even
 * though both are amber. Alphabetical inside that keeps the order stable when
 * nothing else separates two entries.
 */
export function sortWorklist(entries: readonly WorklistEntry[]): WorklistEntry[] {
  const weight = (e: WorklistEntry) => (e.kind === 'group' ? e.memberCount : 1);
  const label = (e: WorklistEntry) => (e.kind === 'group' ? e.causeLabel : e.account.accountName);
  return [...entries].sort((a, b) => {
    const s = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (s !== 0) return s;
    const w = weight(b) - weight(a);
    if (w !== 0) return w;
    return label(a).localeCompare(label(b));
  });
}

/**
 * The Healthy summary line's counts — everything NOT in the worklist.
 *
 * ⚠️ IT COUNTS ACCOUNTS, NOT ENTRIES. A group of fourteen is fourteen accounts
 * needing attention, and reporting "1" there would understate the outage on the
 * one line a reader uses to decide whether to look.
 */
export function countAccountsNeedingAttention(entries: readonly WorklistEntry[]): number {
  let n = 0;
  for (const e of entries) n += e.kind === 'group' ? e.memberCount : 1;
  return n;
}
